import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useNavigate } from "react-router-dom";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import axios from "axios";
import IconHeader from '../../images/logo-lg.png';
import QuoteSlider from "./QuoteSlider";
import "./index.scss";
import apiConfig from "./apiConfig";

const quotes = [
  "Easily discover and quickly benefit from the most advanced technologies.",
  "Expert-level insights at your fingertips.",
  "Expedite planning, fieldwork and completion procedures.",
  "Elevate your expertise and confidence.",
  "AI-powered audits: Save time, increase profitability, and improve quality.",
];

function Login() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRememberTrue, setIsRememberTrue] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Invalid email address"
      )
      .required("Email is required"),
    password: Yup.string()
      .required("Password is required"),
    rememberMe: Yup.boolean(),
  });

  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: false,
    initialValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      if (isRememberTrue) {
        localStorage.setItem("userCred", JSON.stringify(values));
      } else {
        localStorage.removeItem("userCred");
      }

      try {
        setLoading(true);

        const response = await axios.post(
          `${apiConfig.apiBaseURL}/userLogin`,
          {
            email: values.email,
            password: values.password,
          },
          {
            headers: {
              "Content-Type": "application/json",
              "x-api-key": apiConfig["x-api-key"],
            },
          }
        );

        if (response.data.success) {
          // Store the user ID
          localStorage.setItem("userId", response.data.id);
          toast.success(response.data.message, {
            position: toast.POSITION.TOP_RIGHT,
          });

          // window.dispatchEvent(new Event('auth-updated'));

          navigate("/otp-authentication", { state: { email: values.email } });
        }
      } catch (error) {
        console.error("Login failed", error);
        toast.error(error.response?.data?.message || "Login failed", {
          position: toast.POSITION.TOP_RIGHT,
        });
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userCred"));
    if (userData) {
      formik.setValues(userData);
      setIsRememberTrue(true);
    }
  }, []);

  return (
    <section className="credential-page">
      <Container fluid>
        <Row className="justify-content-md-center" style={{ minHeight: "100vh" }}>
          <Col lg={6} className="p-0">
            <div className="left-side d-md-flex align-items-center justify-content-center bg-light">
              <QuoteSlider quotes={quotes} />
            </div>
          </Col>

          <Col lg={6} className="right-side">
            <div className="mb-4 card-title">
              <figure className="text-center mb-5">
                <LazyLoadImage src={IconHeader} alt="image" className="img-fluid" />
              </figure>
              <h5 className="fw-bolder text-dark">Let's Get Started</h5>
            </div>
            <Form onSubmit={formik.handleSubmit} noValidate>
              <Form.Group className="mb-3" controlId="formBasicEmail">
                <Form.Label className="mb-1">
                  Email Address<sup>*</sup>
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="p-3"
                  isInvalid={formik.touched.email && formik.errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3 position-relative" controlId="formBasicPassword">
                <Form.Label className="mb-1">
                  Password<sup>*</sup>
                </Form.Label>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="password p-3"
                  isInvalid={formik.touched.password && formik.errors.password}
                />
                <i
                  onClick={togglePasswordVisibility}
                  className="password-icon"
                  style={{ position: "absolute", right: "31px", top: "41px", cursor: "pointer" }}
                >
                  {showPassword ? <BsEyeSlash /> : <BsEye />}
                </i>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.password}
                </Form.Control.Feedback>
              </Form.Group>

              <div className="d-flex justify-content-between">
                <Form.Group className="mb-3" controlId="formBasicCheckbox">
                  <Form.Check
                    type="checkbox"
                    label="Remember me"
                    name="rememberMe"
                    checked={isRememberTrue}
                    onChange={() => setIsRememberTrue(!isRememberTrue)}
                  />
                </Form.Group>

                <Link to="/request-password" className="text-secondary text-decoration-none fw-bolder">
                  Forgot Password?
                </Link>
              </div>

              <Button
                variant="primary"
                className="mt-4 border-0 text-white font-16 px-5 py-3 w-100"
                type="submit"
                disabled={formik.isSubmitting || loading}
              >
                {loading ? "Logging in..." : "Sign in"}
              </Button>
            </Form>

            <Link to="/create-new-account" className="text-light text-decoration-none text-center d-block mt-4">
              Don't have an account? <span className="text-primary fw-bolder">Sign up</span>
            </Link>
          </Col>
        </Row>

        {/* <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} /> */}
      </Container>
    </section>
  );
}

export default Login;